import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Mail, Globe, Loader2, Search, Shuffle, Save } from "lucide-react";

/**
 * Order an alternative/substitute product for a single request item.
 * Props: open, onOpenChange, request, itemIndex, onDone?
 */
export default function AlternativeOrderDialog({ open, onOpenChange, request, itemIndex, onDone }) {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [form, setForm] = useState({ code: "", name: "", unit: "adet", quantity: "", note: "" });
  const [saveToCatalog, setSaveToCatalog] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const original = (request?.items || [])[itemIndex] || {};

  useEffect(() => {
    if (!open) return;
    (async () => { try { const { data } = await api.get("/suppliers"); setSuppliers(data); } catch (_) {} })();
    const alt = original.alternative;
    if (alt) {
      // Editing / ordering a previously-saved (pending) alternative — prefill it.
      setForm({ code: alt.product_code || "", name: alt.product_name || "", unit: alt.unit || original.unit || "adet", quantity: String(alt.quantity ?? original.quantity ?? ""), note: alt.note || "" });
      setSupplierId(alt.supplier_id || request?.supplier_id || "");
    } else {
      setForm({ code: "", name: "", unit: original.unit || "adet", quantity: String(original.quantity ?? ""), note: "" });
      setSupplierId(request?.supplier_id || "");
    }
    setSaveToCatalog(false); setSearch(""); setSuggestions([]); setSearchOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemIndex]);

  useEffect(() => {
    if (!search.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: search.trim(), limit: 8 } });
        setSuggestions(data); setSearchOpen(true);
      } catch (_) {}
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const pick = (p) => {
    setForm((f) => ({ ...f, code: p.code, name: p.name, unit: p.unit || "adet" }));
    setSearch(""); setSuggestions([]); setSearchOpen(false); setSaveToCatalog(false);
  };

  const supplier = suppliers.find((s) => s.id === supplierId);

  const altMessage = (supName) =>
    `Merhaba ${supName},\n\n*${request.request_no}* — alternatif ürün siparişi:\n1. ${form.code.trim().toUpperCase()} — ${form.name.trim()} • ${form.quantity} ${form.unit}${form.note ? `\n   Not: ${form.note}` : ""}\n\nBilgilerinize.`;

  const openWhatsApp = async (supName, supPhone) => {
    const waNumber = (supPhone || "").replace(/[^\d]/g, "");
    const msg = altMessage(supName);
    const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    await shareToWhatsApp({ text: msg, title: `Talep ${request.request_no}`, waUrl });
  };

  const openMail = (supName, supEmail) => {
    const subject = `Alternatif Sipariş: ${request.request_no}`;
    const body = altMessage(supName);
    const params = new URLSearchParams();
    params.set("subject", subject); params.set("body", body);
    if (user?.email) params.set("cc", user.email);
    const mailUrl = `mailto:${encodeURIComponent(supEmail || "")}?${params.toString().replace(/\+/g, "%20")}`;
    const a = document.createElement("a"); a.href = mailUrl; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const submit = async (channel) => {
    const code = form.code.trim().toUpperCase();
    const name = form.name.trim();
    if (!code || !name) { toast.error("Alternatif ürün kodu ve açıklaması gerekli"); return; }
    const qty = Number(form.quantity);
    if (!qty || qty <= 0) { toast.error("Geçerli miktar girin"); return; }
    if (channel && !supplierId) { toast.error("Sipariş için tedarikçi seçin"); return; }
    const supName = supplier?.name || "";
    if (channel === "whatsapp") await openWhatsApp(supName, supplier?.phone);
    else if (channel === "mail") openMail(supName, supplier?.email);
    setBusy(channel || "save");
    try {
      await api.post(`/requests/${request.id}/order-alternative`, {
        item_index: itemIndex,
        alt_code: code, alt_name: name, alt_unit: form.unit || "adet",
        alt_quantity: qty, supplier_id: supplierId || null, channel: channel || null,
        save_to_catalog: saveToCatalog, note: form.note || "",
      });
      toast.success(channel ? `Alternatif ürün ${supName} tedarikçisine sipariş verildi` : "Alternatif kaydedildi — sonra sipariş verebilirsiniz");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="alternative-order-dialog" className="max-w-md p-4 sm:p-6 max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shuffle className="h-5 w-5 text-indigo-600" /> Alternatif Ürün Sipariş Ver
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            <span className="font-semibold text-slate-700">{original.product_code}</span> — {original.product_name} için alternatif ürün seçin. Orijinal ürün "alternatif ile karşılandı" olarak işaretlenir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Catalog search (optional helper) */}
          <div className="relative">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Katalogdan ara (opsiyonel)</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="alt-search-input"
                className="pl-9 h-11"
                placeholder="Kod veya ad ile ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => suggestions.length && setSearchOpen(true)}
              />
            </div>
            {searchOpen && suggestions.length > 0 && (
              <div data-testid="alt-search-results" className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-y-auto thin-scroll">
                {suggestions.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`alt-result-${p.code}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(p)}
                    className="w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 text-sm">{p.code}</div>
                      <div className="text-xs text-slate-500 truncate">{p.name}</div>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">{p.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Alternatif Kod</Label>
              <Input data-testid="alt-code" className="mt-1.5 h-11" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Birim</Label>
              <Input data-testid="alt-unit" className="mt-1.5 h-11" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Açıklama</Label>
            <Input data-testid="alt-name" className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Miktar</Label>
              <Input data-testid="alt-quantity" type="number" inputMode="decimal" step="any" min="0" className="mt-1.5 h-11" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Not (ops.)</Label>
              <Input data-testid="alt-note" className="mt-1.5 h-11" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input type="checkbox" data-testid="alt-save-catalog" checked={saveToCatalog} onChange={(e) => setSaveToCatalog(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-slate-900" />
            Bu ürünü ürün kataloğuna da kaydet
          </label>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="alt-supplier-select" className="mt-1.5 h-11">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`alt-supplier-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="alt-save-btn"
            onClick={() => submit(null)}
            disabled={!!busy}
            variant="outline"
            className="w-full h-11 gap-2 border-slate-300 text-slate-800 hover:bg-slate-100"
          >
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet (sonra sipariş ver)
          </Button>

          <div className="flex items-center gap-2 pt-1">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Kaydet ve şimdi sipariş ver</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button data-testid="alt-order-whatsapp" onClick={() => submit("whatsapp")} disabled={!!busy || !supplierId} className="h-11 gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white">
              {busy === "whatsapp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} WhatsApp
            </Button>
            <Button data-testid="alt-order-mail" onClick={() => submit("mail")} disabled={!!busy || !supplierId} variant="outline" className="h-11 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50">
              {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Mail
            </Button>
            <Button data-testid="alt-order-b2b" onClick={() => submit("b2b")} disabled={!!busy || !supplierId} variant="outline" className="h-11 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              {busy === "b2b" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} B2B
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
